import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { DeviceSummary } from '../devices/useDevices'
import { SeriesCard } from './SeriesCard'
import type { LibrarySeriesEntry } from './useLibrary'
import { useVirtualizedGrid } from './useVirtualizedGrid'
import { useGridKeyboardNav } from './useGridKeyboardNav'

interface SeriesGridProps {
  entries: LibrarySeriesEntry[]
  devices: Map<string, DeviceSummary>
  loading: boolean
  emptyMessage: string
  onOpenDetail: (tmdbId: number, type: 'tv') => void
  keyboardEnabled: boolean
}

/**
 * Virtualized library Series grid. Mirrors `MovieGrid` but sources
 * `LibrarySeriesEntry` records and dispatches to `SeriesCard`. See
 * `MovieGrid` for the behavioural notes — both grids share the same
 * underlying `useVirtualizedGrid` + `useGridKeyboardNav` pair.
 */
export function SeriesGrid({
  entries,
  devices,
  loading,
  emptyMessage,
  onOpenDetail,
  keyboardEnabled,
}: SeriesGridProps) {
  const itemCount = entries.length
  const { containerRef, layout, visibleRows } = useVirtualizedGrid({
    itemCount,
    gap: 16,
  })

  const [focusedIndex, setFocusedIndex] = useState(-1)
  const previousTmdbIdRef = useRef<number | null>(null)
  const focusedCardRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (itemCount === 0) {
      if (focusedIndex !== -1) setFocusedIndex(-1)
      previousTmdbIdRef.current = null
      return
    }
    const prevId = previousTmdbIdRef.current
    if (prevId != null) {
      const newIndex = entries.findIndex((e) => e.tmdbId === prevId)
      if (newIndex >= 0 && newIndex !== focusedIndex) {
        setFocusedIndex(newIndex)
        return
      }
    }
    if (focusedIndex >= itemCount) {
      setFocusedIndex(itemCount - 1)
    }
  }, [entries, itemCount, focusedIndex])

  useEffect(() => {
    if (focusedIndex >= 0 && focusedIndex < entries.length) {
      previousTmdbIdRef.current = entries[focusedIndex].tmdbId
    }
  }, [focusedIndex, entries])

  const getColumns = useCallback(() => layout.columns, [layout.columns])
  const handleActivate = useCallback(
    (index: number) => {
      const entry = entries[index]
      if (entry) onOpenDetail(entry.tmdbId, 'tv')
    },
    [entries, onOpenDetail]
  )

  useGridKeyboardNav({
    enabled: keyboardEnabled && !loading && itemCount > 0,
    itemCount,
    focusedIndex,
    setFocusedIndex,
    getColumns,
    onActivate: handleActivate,
  })

  useEffect(() => {
    if (focusedIndex < 0) return
    const node = focusedCardRef.current
    if (!node) return
    node.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
  }, [focusedIndex])

  const rows = useMemo(() => {
    if (itemCount === 0 || layout.cardWidth === 0) return []
    return visibleRows.map((rowIndex) => {
      const cols: { entry: LibrarySeriesEntry; index: number; col: number }[] = []
      for (let col = 0; col < layout.columns; col++) {
        const idx = rowIndex * layout.columns + col
        if (idx >= itemCount) break
        cols.push({ entry: entries[idx], index: idx, col })
      }
      return { rowIndex, cols }
    })
  }, [entries, itemCount, layout.columns, layout.cardWidth, visibleRows])

  if (loading) {
    return <p className="py-12 text-center text-text-tertiary">Loading…</p>
  }
  if (itemCount === 0) {
    return <p className="py-12 text-center text-text-tertiary">{emptyMessage}</p>
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      style={{ height: layout.totalHeight }}
    >
      {rows.map(({ rowIndex, cols }) => (
        <div
          key={rowIndex}
          className="absolute left-0 right-0 flex"
          style={{
            top: rowIndex * layout.rowHeight,
            height: layout.cardHeight,
            gap: layout.gap,
          }}
        >
          {cols.map(({ entry, index }) => (
            <div
              key={entry.tmdbId}
              ref={index === focusedIndex ? focusedCardRef : undefined}
              style={{ width: layout.cardWidth, flex: '0 0 auto' }}
            >
              <SeriesCard
                tmdbId={entry.tmdbId}
                record={entry.record}
                devices={devices}
                onOpen={onOpenDetail}
                focused={index === focusedIndex}
                onFocus={() => setFocusedIndex(index)}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
